import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormikContext } from 'formik';
import Dropdown from '../../../../components/Dropdown';
import InputField from '../../../../components/InputField';
import DatePicker from '../../../../components/DatePicker/DatePicker';
import Toggle from '../../../../components/Toggle/Toggle';
import {
	useGetRecruitingManagersQuery,
	useGetRolesQuery,
	useGetStagesQuery,
	useGetStatesQuery,
} from '../../../../store/apiSlices/recruitApiSlice';
import { setRecruitForm } from '../../../../store/reducers/recruitFormReducer';
import { RootState } from '../../../../store/store';
import { DropdownModel } from '../../../../models/dropdown.model';
import { OfficerRoles } from '../../../../enums/enum';
import SearchableDropdown from '../../../../components/SearchableDropdown';

const GeneralDetails = () => {
	const dispatch = useDispatch();
	const addOrUpdateRecruit = useSelector(
		(state: RootState) => state.addOrUpdateRecruit,
	);
	const userInfo = useSelector((state: RootState) => state.user);
	const maxDate = new Date();
	const stagesMasterData = useGetStagesQuery(null);
	const rolesMasterData = useGetRolesQuery(null);
	const recruitingManagersMasterData = useGetRecruitingManagersQuery({
		userEmail: userInfo.user_email,
		isSysAdmin: !userInfo.roles.adminAppAccessedUserListInclusion,
	});
	const statesMasterData = useGetStatesQuery(null);
	const [recruitingManagers, setRecruitingManagers] = useState<
		DropdownModel[]
	>([]);
	const { getFieldProps, getFieldMeta, getFieldHelpers } = useFormikContext();
	const [disableProducing, setDisableProducing] = useState(false);

	useEffect(() => {
		if (
			!recruitingManagersMasterData.currentData?.value?.length ||
			!addOrUpdateRecruit ||
			!userInfo
		)
			return;
		const recruitingManagers =
			recruitingManagersMasterData.currentData.value;
		const RM = addOrUpdateRecruit.isAddingRecruit
			? recruitingManagers.find((e) => e.id === userInfo?.id)?.id
			: null;
		if (RM) {
			dispatch(
				setRecruitForm({
					...getFieldProps('').value,
					general_details: {
						...getFieldProps('general_details').value,
						recruiting_manager_id: RM,
					},
				}),
			);
		}
		setRecruitingManagers([...recruitingManagers]);
	}, [recruitingManagersMasterData, addOrUpdateRecruit, userInfo]);

	useEffect(() => {
		if (
			getFieldProps('general_details.role_id').value ===
			rolesMasterData.currentData?.value?.find(
				(e) => e.value === OfficerRoles.loanOfficer,
			)?.id
		) {
			getFieldHelpers('general_details.is_producing').setValue(true);
			setDisableProducing(true);
		} else {
			const isProducing = getIsProducing();
			getFieldHelpers('general_details.is_producing').setValue(
				isProducing,
			);
			setDisableProducing(false);
		}
	}, [getFieldProps('general_details.role_id').value]);

	const getIsProducing = () => {
		const initialValue = getFieldMeta(
			'general_details.is_producing',
		).initialValue;
		const currentValue = getFieldMeta('general_details.is_producing').value;
		const isInitialBranch =
			getFieldMeta('general_details.role_id').initialValue ===
			rolesMasterData.currentData?.value?.find(
				(e) => e.value === OfficerRoles.branch,
			)?.id;
		return (
			initialValue === true && currentValue === true && isInitialBranch
		);
	};

	return (
		<div className="flex flex-col bg-white p-4 gap-y-4 rounded-lg">
			<div className="text-xl font-bold">General Details</div>
			<div className="grid  grid-cols-2 gap-4">
				<InputField
					name="general_details.name"
					label="Name"
					placeholder="Enter Name"
					disabled={addOrUpdateRecruit.isRecruitFieldsDisabled}
				/>
				<Dropdown
					label="Stage"
					name="general_details.stage_id"
					placeholder="Select Stage"
					list={stagesMasterData.currentData?.value || []}
					isRequired={false}
					disabled={addOrUpdateRecruit.isRecruitFieldsDisabled}
				/>
				<InputField
					name="general_details.email"
					label="Email"
					placeholder="Enter Email"
					isRequired={false}
					disabled={addOrUpdateRecruit.isRecruitFieldsDisabled}
				/>
				<InputField
					name="general_details.phone"
					label="Phone Number"
					placeholder="Enter Phone Number"
					maxLength={17}
					phoneNumber={true}
					disabled={addOrUpdateRecruit.isRecruitFieldsDisabled}
				/>
				<Dropdown
					label="Role"
					name="general_details.role_id"
					placeholder="Select Role"
					list={rolesMasterData.currentData?.value || []}
					disabled={addOrUpdateRecruit.isRecruitFieldsDisabled}
				/>
				<InputField
					label="Current Company"
					name="general_details.current_company"
					placeholder="Enter Company"
					isRequired={false}
					disabled={addOrUpdateRecruit.isRecruitFieldsDisabled}
				/>
				<InputField
					name="general_details.city"
					label="City"
					isRequired={false}
					placeholder="Enter City"
					disabled={addOrUpdateRecruit.isRecruitFieldsDisabled}
				/>
				<SearchableDropdown
					label="State"
					name="general_details.state_id"
					placeholder="Select State"
					isRequired={false}
					list={statesMasterData?.currentData?.value || []}
					disabled={addOrUpdateRecruit.isRecruitFieldsDisabled}
				/>
				<Dropdown
					label="Recruiting Manager"
					name="general_details.recruiting_manager_id"
					placeholder="Select Recruiting Manager"
					isRequired={false}
					list={recruitingManagers}
					disabled={
						!userInfo.roles.adminAppEditOthersOwnedRecruit ||
						addOrUpdateRecruit.isRecruitFieldsDisabled
					}
				/>
				<DatePicker
					name="general_details.first_contacted_date"
					label="First Contacted Date"
					placeholder="Select Date"
					maxDate={maxDate}
					disabled={addOrUpdateRecruit.isRecruitFieldsDisabled}
				/>
				{!addOrUpdateRecruit?.isAddingRecruit ? (
					<>
						<DatePicker
							name="general_details.last_contacted_date"
							label="Last Activity Date"
							placeholder="Select Date"
							disabled={true}
						/>
					</>
				) : null}
				<InputField
					label="NMLS Id"
					name="general_details.nmls_id"
					placeholder="Enter NMLS Id"
					isRequired={false}
					maxLength={9}
					disabled={addOrUpdateRecruit.isRecruitFieldsDisabled}
				/>
				<div className="flex">
					<Toggle
						name="general_details.is_producing"
						label="Producing"
						isRequired={false}
						disabled={
							disableProducing ||
							addOrUpdateRecruit.isRecruitFieldsDisabled
						}
					/>
				</div>
			</div>
		</div>
	);
};
export default GeneralDetails;
